import React from "react"
import { useTranslation } from "react-i18next"
import { Navigate } from "react-router-dom"

import { connect } from "scrivito"
import { ProjectPageObj } from "scrivito/Objs/ProjectPage/ProjectPageObjClass"
import { NOT_FOUND } from "shared/constants/permalinks"
import { useTitle } from "shared/lib/usePageTitle"
import { AnchorType } from "shared/types/NavigationAnchor"

import { useProjectData } from "entities/projects/api/useProjectData"
import { useAnchor } from "features/navigation/model/useAnchor"
import { useNavigateToPermalink } from "features/navigation/model/useNavigateToPermalink"
import { useScrollController } from "features/scroll-management/model/useScrollController"

import { TableAPIContextProvider } from "../../shared/ui/table/TableAPIContext"
import { ProjectPage } from "./ProjectPage"
import { ProjectChatContextProvider } from "./chat-context/ProjectChatContext"
import { useProjectPageModel } from "./hooks/useProjectPageModel"

export interface ProjectPageConnectorProps {
  projectID: string
  page: ProjectPageObj
}

function ProjectPageConnectorInner(props: ProjectPageConnectorProps) {
  useScrollController({ restoreOnPush: true })
  return <ProjectPage {...useProjectPageModel(props)} />
}

const ConnectedProjectPageConnectorInner = React.memo(
  connect(ProjectPageConnectorInner)
)

export const ProjectPageConnector = React.memo(
  (props: ProjectPageConnectorProps) => {
    const { t } = useTranslation()
    const { projectID } = props
    const { project, isLoading } = useProjectData(projectID || null)
    const { href } = useNavigateToPermalink(NOT_FOUND)

    useTitle(project?.title || t("Project.Project"))
    useAnchor(AnchorType.PROJECT)

    if (((!project || !project.id) && !isLoading) || !projectID) {
      return <Navigate to={href} />
    }

    return (
      <TableAPIContextProvider>
        <ProjectChatContextProvider>
          <ConnectedProjectPageConnectorInner key={projectID} {...props} />
        </ProjectChatContextProvider>
      </TableAPIContextProvider>
    )
  }
)
